import { algoliasearch } from 'algoliasearch';
import { IToolCatalogPrices, IToolEstimatedDelivery, IToolSearchResult, TToolStatus } from 'types/quoteEditor.types';

const { REACT_APP_ALGOLIA_APP_ID, REACT_APP_ALGOLIA_API_KEY, REACT_APP_ALGOLIA_INDEX_NAME } = process.env;

interface IAlgoliaProductHit {
  productId: number | null;
  productName: string | null;
  sku: string | null;
  imageUrl: string | null;
  categoryId?: number | null;
  categoryName: string | null;
  brandName: string | null;
  isActive?: boolean;
  isQuoteProduct?: boolean;
  pills?: Record<string, string> | null;
  processor?: string | null;
  ram?: string | null;
  storage?: string | null;
  prices?: {
    pricePrimeUsd?: string | number | null;
    localPrice?: string | number | null;
  } | null;
  estimatedDeliveryDate?: {
    minDeliverTime?: number | null;
    maxDeliverTime?: number | null;
    deliveryPeriod?: string | null;
  } | null;
}

/** Deriva el estado del producto a partir de los flags de Algolia. */
const mapStatus = (hit: IAlgoliaProductHit): TToolStatus => {
  if (hit.isQuoteProduct) return 'forQuote';
  if (hit.isActive) return 'active';
  return 'inactive';
};

/** Construye IToolCatalogPrices a partir de los precios del hit, si están disponibles. */
const mapCatalogPrices = (hit: IAlgoliaProductHit): IToolCatalogPrices | null => {
  const platinumRaw = hit.prices?.pricePrimeUsd;
  if (platinumRaw == null) return null;
  const platinumUsd = parseFloat(String(platinumRaw));
  if (isNaN(platinumUsd) || platinumUsd <= 0) return null;
  return { platinumUsd };
};

/** Construye IToolEstimatedDelivery a partir de los datos del hit, si están disponibles. */
const mapEstimatedDelivery = (hit: IAlgoliaProductHit): IToolEstimatedDelivery | null => {
  const min = hit.estimatedDeliveryDate?.minDeliverTime;
  const max = hit.estimatedDeliveryDate?.maxDeliverTime;
  if (min == null) return null;
  return {
    minTime: min,
    maxTime: max ?? min,
    period: hit.estimatedDeliveryDate?.deliveryPeriod ?? 'days'
  };
};

/** Mapea un hit de Algolia al tipo IToolSearchResult usado en el QuoteEditor. */
const mapHitToToolSearchResult = (hit: IAlgoliaProductHit): IToolSearchResult => ({
  id: hit.productId ?? Date.now(),
  name: hit.productName ?? '',
  sku: hit.sku ?? '',
  imageUrl: hit.imageUrl ?? undefined,
  category: hit.categoryName ?? '',
  categoryId: hit.categoryId ?? null,
  brand: hit.brandName ?? undefined,
  processor: hit.processor ?? undefined,
  ram: hit.ram ?? undefined,
  storage: hit.storage ?? undefined,
  pills: hit.pills ?? null,
  status: mapStatus(hit),
  catalogPrices: mapCatalogPrices(hit),
  estimatedDelivery: mapEstimatedDelivery(hit)
});

/**
 * Servicio de búsqueda de productos via Algolia.
 * Replica el patrón de mono-crm adaptado al contexto del backoffice.
 */
export const algoliaService = () => {
  const client = algoliasearch(String(REACT_APP_ALGOLIA_APP_ID), String(REACT_APP_ALGOLIA_API_KEY));

  /**
   * Busca productos por nombre o SKU con filtros de país y estado activo.
   * Aplica: countryCode:xx AND (isActive:true OR isQuoteProduct:true)
   */
  const searchProducts = async (query: string, countryCode?: string): Promise<IToolSearchResult[]> => {
    const filters = countryCode ? `countryCode:${countryCode} ` : undefined;
    const { results } = await client.searchForHits<IAlgoliaProductHit>({
      requests: [{ indexName: String(REACT_APP_ALGOLIA_INDEX_NAME), query, filters }]
    });
    const hits = results?.flatMap(result => result.hits) ?? [];
    return hits.map(mapHitToToolSearchResult);
  };

  return { searchProducts };
};

export default algoliaService;
